import React, { useEffect, useRef, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import nintendoSearchService from "../../service/nintendoSearch.service";
import HeartButton from "../../favorites/HeartButton";
import { favoriteFromNintendoGame } from "../../favorites/favoriteKey";
import NintendoPickedGame, {
  Cover,
  PriceLine,
  Switch2Badge,
  money,
  panelClass,
  parseAmount,
  plural,
  withLivePrice,
} from "./NintendoPickedGame";
import RegionSelect from "./RegionSelect";
import { matchNintendoConsole } from "./NintendoConsoleFilter";

const SEARCH_PLACEHOLDER = "Найти игру: «зельда», «ведьмак», «марио кролики»…";
const REGION_CURRENCY = {
  us: "USD",
  ca: "CAD",
  br: "BRL",
  fr: "EUR",
  pl: "PLN",
  se: "SEK",
  hk: "HKD",
};

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function regionsNote(data) {
  const labels = (data?.regions || []).map((item) => item.label).join(", ");
  return labels
    ? `Поиск по играм и подбор «Остаток в 0» пока работают для регионов: ${labels}.`
    : "Для этого региона поиск пока недоступен.";
}

function SpendToggle({ on, onChange, className }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      title="Подобрать игры, чтобы потратить остаток баланса без остатка"
      className={`shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-sm font-medium transition ${
        on
          ? "bg-indigo-600 text-white shadow-sm hover:bg-indigo-500"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
      } ${className}`}
    >
      <WalletIcon className="h-4 w-4" />
      Остаток в 0
    </button>
  );
}

function SpendResults({ data, amount, region, label }) {
  if (!data) return null;
  const note = (text) => (
    <p className="px-1 text-sm text-gray-500 dark:text-gray-400">{text}</p>
  );
  if (data.error) return note("Подбор временно недоступен, попробуйте позже.");
  if (data.supported === false) return note(regionsNote(data));
  if (data.ready === false) {
    return note("Собираем цены каталога для этого региона — загляните чуть позже.");
  }
  const currency = data.currency;
  if (data.max && (amount < data.min || amount > data.max)) {
    return note(
      `Введите сумму от ${money(data.min, currency)} до ${money(data.max, currency)}.`,
    );
  }
  if (!data.results?.length) return note("Не нашлось сочетаний на эту сумму.");

  return (
    <div className="space-y-2">
      {data.results.map((variant) => (
        <SpendVariant
          key={variant.items.map((item) => item.nsuid).join("-")}
          variant={variant}
          currency={currency}
          region={region}
          label={label}
        />
      ))}
    </div>
  );
}

const ESHOP_LANG = { us: "en", ca: "en", br: "pt", fr: "fr", pl: "pl", se: "sv", hk: "zh" };

function eshopUrl(item, region) {
  if (item.url) return item.url;
  return `https://ec.nintendo.com/${region.toUpperCase()}/${ESHOP_LANG[region] || "en"}/titles/${item.nsuid}`;
}

/** Открывает страницы игр в eShop; window.open без noopener — иначе не узнать, что вкладку заблокировали. */
function openTabs(items, region) {
  return items.filter((item) => {
    const tab = window.open(eshopUrl(item, region), "_blank");
    if (!tab) return true;
    tab.opener = null;
    return false;
  });
}

function SpendVariant({ variant, currency, region, label }) {
  const [blocked, setBlocked] = useState(null);

  const buy = () => setBlocked(openTabs(variant.items, region));
  const openOne = (item) =>
    setBlocked((current) => current.filter((other) => other.nsuid !== item.nsuid));

  return (
    <div className={`${panelClass} px-3 py-2`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex shrink-0 -space-x-3">
          {variant.items.map((item) => (
            <Cover
              key={item.nsuid}
              src={item.image}
              className="h-10 w-10 border-2 border-white dark:border-[#2a2a2a]"
            />
          ))}
        </div>
        <div className="min-w-0 flex-1 text-sm leading-snug">
          {variant.items.map((item, index) => (
            <React.Fragment key={item.nsuid}>
              {index > 0 ? <span className="text-gray-400"> + </span> : null}
              <a
                href={item.seoPath || item.url}
                {...(item.seoPath ? {} : { target: "_blank", rel: "noreferrer" })}
                className="hover:underline"
              >
                {item.title}
              </a>
              {item.dlc ? <span className="text-[10px] text-gray-400"> DLC</span> : null}{" "}
              <span className="whitespace-nowrap text-gray-500 dark:text-gray-400">
                {money(item.price, currency)}
              </span>
            </React.Fragment>
          ))}
        </div>
        <div className="flex w-full shrink-0 items-center justify-between gap-1 sm:w-auto sm:flex-col sm:items-end">
          <span
            className={`whitespace-nowrap text-[11px] font-semibold ${
              variant.leftover ? "text-gray-500" : "text-green-600"
            }`}
          >
            {variant.leftover
              ? `останется ${money(variant.leftover, currency)}`
              : "ровно в ноль"}
          </span>
          <button
            type="button"
            onClick={buy}
            title={`Откроет ${
              variant.items.length > 1 ? "страницы игр" : "страницу игры"
            } в eShop${label ? ` (${label})` : ""} в новых вкладках`}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-indigo-700"
          >
            Перейти к покупке
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {blocked?.length ? (
        <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          <p>
            Браузер открыл {variant.items.length - blocked.length} из {variant.items.length}{" "}
            вкладок. Откройте остальные кнопками ниже или разрешите всплывающие окна для
            сайта, чтобы в следующий раз всё открывалось одним нажатием.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {blocked.map((item) => (
              <a
                key={item.nsuid}
                href={eshopUrl(item, region)}
                target="_blank"
                rel="noreferrer"
                onClick={() => openOne(item)}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 font-medium text-gray-900 hover:border-indigo-400 dark:border-amber-700 dark:bg-[#2a2a2a] dark:text-white"
              >
                {item.title}
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400" />
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Строка поиска с регионом и переключателем «Остаток в 0»: выключен — поиск игр, включён — подбор на остаток. */
function NintendoGameSearch({
  region,
  regions = [],
  onRegionChange,
  cards: cardsApi,
  externalPick,
  initialQuery = "",
  consoleFilter = "all",
  onClosePick,
}) {
  const [spendMode, setSpendMode] = useState(false);
  const [values, setValues] = useState({ search: initialQuery, spend: "" });
  const [search, setSearch] = useState(null);
  const [spend, setSpend] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [spendLoading, setSpendLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [balance, setBalance] = useState("");
  const [dlc, setDlc] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const query = useDebounced(values.search.trim(), 200);
  const spendText = useDebounced(values.spend, 350);
  const amount = parseAmount(spendText);
  const currency = REGION_CURRENCY[region] || "USD";

  useEffect(() => {
    setPicked(null);
    setBalance("");
  }, [region]);

  useEffect(() => {
    if (!externalPick) return;
    setSpendMode(false);
    setOpen(false);
    setPicked(externalPick);
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [externalPick]);

  const liveNsuid = picked && !picked.live ? picked.game.nsuid : null;
  const liveRegion = picked?.context.region;
  useEffect(() => {
    if (!liveNsuid || !liveRegion) return undefined;
    const controller = new AbortController();
    const apply = (live) =>
      setPicked((current) =>
        current && !current.live && current.game.nsuid === liveNsuid
          ? withLivePrice(current, live)
          : current,
      );
    nintendoSearchService
      .livePrice(liveRegion, liveNsuid, controller.signal)
      .then(apply)
      .catch((error) => {
        if (error.name !== "AbortError") apply(null);
      });
    return () => controller.abort();
  }, [liveNsuid, liveRegion]);

  useEffect(() => {
    if (spendMode || query.length < 2) {
      setSearch(null);
      setSearchLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearchLoading(true);
    nintendoSearchService
      .search(region, query, controller.signal)
      .then((data) => {
        setSearch({ ...data, region });
        setOpen(true);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setSearch({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false);
      });
    return () => controller.abort();
  }, [query, region, spendMode]);

  useEffect(() => {
    if (!spendMode || amount <= 0) {
      setSpend(null);
      setSpendLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setSpendLoading(true);
    nintendoSearchService
      .spend(region, amount, dlc, controller.signal)
      .then((data) => setSpend({ ...data, region }))
      .catch((error) => {
        if (error.name !== "AbortError") setSpend({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setSpendLoading(false);
      });
    return () => controller.abort();
  }, [amount, region, dlc, spendMode]);

  useEffect(() => {
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const switchMode = (on, spendValue) => {
    setSpendMode(on);
    if (spendValue != null) {
      setValues((current) => ({ ...current, spend: spendValue }));
    }
    setOpen(false);
    inputRef.current?.focus();
  };

  const pick = (game) => {
    setPicked({
      game,
      context: {
        region: search.region,
        currency: search.currency,
        label: search.label,
        cardRegion: search.cardRegion,
        cards: search.cards,
      },
    });
    setOpen(false);
  };

  const regionSpend = spend?.region === region ? spend : null;
  const searchHits = (search?.results || []).filter((game) =>
    matchNintendoConsole(game, consoleFilter),
  );
  const spendHits = (regionSpend?.results || []).filter((variant) =>
    (variant.items || []).every((item) => matchNintendoConsole(item, consoleFilter)),
  );
  const regionSpendView = regionSpend
    ? { ...regionSpend, results: spendHits }
    : null;
  const meta = (spendMode ? spendLoading : searchLoading)
    ? spendMode
      ? "подбираем…"
      : "ищем…"
    : spendMode && spendHits.length
      ? `${money(amount, spend.currency)} · ${spendHits.length} ${plural(
          spendHits.length,
          "вариант",
          "варианта",
          "вариантов",
        )} · цены сверены с eShop`
      : "";

  const ModeIcon = spendMode ? WalletIcon : MagnifyingGlassIcon;

  return (
    <section ref={boxRef} className="mx-auto mt-5 max-w-3xl px-1">
      <div className="relative">
        <div className="flex items-stretch rounded-2xl border border-gray-200 bg-white shadow-sm transition focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100 dark:border-gray-600 dark:bg-[#2a2a2a] dark:focus-within:ring-indigo-900/50">
          {regions.length > 1 ? (
            <RegionSelect
              value={region}
              regions={regions}
              onChange={(value) => onRegionChange?.(value)}
            />
          ) : null}
          <ModeIcon className="ml-3 h-5 w-5 shrink-0 self-center text-gray-400" />
          <input
            ref={inputRef}
            value={spendMode ? values.spend : values.search}
            onChange={(e) =>
              setValues((current) => ({
                ...current,
                [spendMode ? "spend" : "search"]: e.target.value,
              }))
            }
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            inputMode={spendMode ? "decimal" : "text"}
            autoComplete="off"
            aria-label={spendMode ? "Остаток на балансе eShop" : "Поиск игры"}
            placeholder={
              spendMode
                ? `Остаток на балансе в ${currency}, например 12.37`
                : SEARCH_PLACEHOLDER
            }
            className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-base text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
          />
          <SpendToggle on={spendMode} onChange={switchMode} className="m-1.5 hidden sm:flex" />
        </div>

        {!spendMode && open && search?.region === region ? (
        <div className={`${panelClass} absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden`}>
          {search.error ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              Поиск временно недоступен, попробуйте позже.
            </p>
          ) : search.supported === false ? (
            <p className="px-3 py-3 text-sm text-gray-500">{regionsNote(search)}</p>
          ) : searchHits.length ? (
            searchHits.map((game) => (
              <div
                key={game.nsuid}
                className="flex items-center hover:bg-indigo-50 dark:hover:bg-gray-700"
              >
                <button
                  type="button"
                  onClick={() => pick(game)}
                  className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-left"
                >
                  <Cover src={game.image} className="h-12 w-12" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900 dark:text-white">
                      {game.title}
                      {game.switch2 ? <Switch2Badge /> : null}
                    </span>
                    <span className="block text-sm">
                      <PriceLine game={game} currency={search.currency} />
                    </span>
                  </span>
                </button>
                <HeartButton
                  item={favoriteFromNintendoGame(game, search.region, search.currency)}
                  className="mx-3"
                />
              </div>
            ))
          ) : (
            <p className="px-3 py-3 text-sm text-gray-500">
              Ничего не нашлось. Попробуйте английское название или другое написание.
            </p>
          )}
        </div>
        ) : null}
      </div>

      <div className="mt-2 flex min-h-[1.5rem] flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-gray-400">
        <SpendToggle on={spendMode} onChange={switchMode} className="flex py-1.5 sm:hidden" />
        {spendMode ? (
          <label className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={dlc}
              onChange={(e) => setDlc(e.target.checked)}
            />
            с дополнениями (DLC)
          </label>
        ) : (
          <span className="hidden sm:inline">
            «Остаток в 0» подберёт игры, чтобы потратить остаток баланса eShop полностью
          </span>
        )}
        <span className="ml-auto">{meta}</span>
      </div>

      {spendMode ? (
        <div className="mt-1">
          <SpendResults
            data={regionSpendView}
            amount={amount}
            region={region}
            label={regions.find((item) => item.value === region)?.label}
          />
        </div>
      ) : picked ? (
        <NintendoPickedGame
          key={`${picked.game.nsuid || picked.game.title}-${picked.context.cardRegion}`}
          favorite={favoriteFromNintendoGame(
            picked.game,
            picked.context.region,
            picked.context.currency,
          )}
          game={picked.game}
          context={picked.context}
          live={picked.live}
          catalog={cardsApi?.cardsFor(picked.context.cardRegion)}
          initialOpen={Boolean(picked.openCheckout)}
          onRefreshCards={cardsApi?.refresh}
          balance={balance}
          onBalance={setBalance}
          onSpendLeft={(left) => switchMode(true, String(left))}
          onClose={() => {
            setPicked(null);
            onClosePick?.();
          }}
        />
      ) : null}
    </section>
  );
}

export default NintendoGameSearch;
